// Precise multi-sample GPS helper.
//
// Why: A single `navigator.geolocation.getCurrentPosition()` reading on
// Android often returns a cached / Wi-Fi / cell-tower fix with ±500-2000 m
// accuracy — which then incorrectly flags a user as "outside their office
// geofence" even when they are physically inside. To prevent that we take
// MULTIPLE samples over a short window and let the backend decide:
//
//   "Inside if ANY good sample (accuracy ≤ 75 m) is inside the geofence."
//
// Usage:
//   const loc = await getPreciseLocation({ onProgress: (i, total) => ... });
//   // loc = { latitude, longitude, accuracy, samples: [{lat,lon,accuracy,timestamp}], aborted? }
//   await fetch(API_URL + '/api/attendance/checkin', {
//     ...,
//     body: JSON.stringify(loc),     // sends latitude/longitude/samples — extra keys are ignored
//   });

// Tuning — must match backend `GPS_TRUST_ACCURACY_M` in utils/geofence.py.
export const SAMPLE_COUNT = 5;
export const SAMPLE_INTERVAL_MS = 1200;     // spacing between samples in the SLOW path
export const SAMPLE_TIMEOUT_MS = 10000;     // per-sample GPS timeout
export const TRUST_ACCURACY_M = 75;         // reject worse-than-this for inside/outside decision

// Jul 4, 2026 (v2.4.3 perf) — adaptive early-exit thresholds. If the first
// two samples are BOTH accurate enough AND close to each other (i.e. GPS
// has clearly locked on), we bail out early and return in ~2 s instead of
// waiting the full 6 s for the remaining 3 samples. Under morning-rush
// this drops check-in latency from ~7-15 s to ~2-4 s per staff.
const EARLY_EXIT_MIN_SAMPLES = 2;
const EARLY_EXIT_AGREE_METERS = 40;   // if 2 trusted samples are within 40m of each other, done
const FAST_SAMPLE_INTERVAL_MS = 500;  // shorter gap between first & second sample

function oneShot() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
          timestamp: new Date(pos.timestamp || Date.now()).toISOString(),
        });
      },
      () => resolve(null),
      { enableHighAccuracy: true, maximumAge: 0, timeout: SAMPLE_TIMEOUT_MS }
    );
  });
}

// Pick the canonical primary lat/lon to send. Prefer the trusted sample with
// the lowest accuracy; otherwise fall back to whatever fix we managed to get.
function pickBest(samples) {
  if (!samples.length) return null;
  const trusted = samples.filter((s) => s.accuracy != null && s.accuracy <= TRUST_ACCURACY_M);
  const pool = trusted.length ? trusted : samples;
  // Lowest accuracy number = most accurate.
  const withAcc = pool.filter((s) => s.accuracy != null);
  if (withAcc.length) return withAcc.reduce((a, b) => (a.accuracy <= b.accuracy ? a : b));
  return pool[0];
}

// Haversine (metres) between two lat/lng points — used for the adaptive
// early-exit check (are the last two trusted samples close enough to
// each other that we can stop sampling now?).
function _distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Take SAMPLE_COUNT GPS readings spaced by SAMPLE_INTERVAL_MS and resolve to:
 *   { latitude, longitude, accuracy, samples }
 *
 * v2.4.3 (Jul 4, 2026) — adaptive early-exit:
 *   If the first two trusted samples (accuracy ≤ 75 m) are within 40 m of
 *   each other, we bail out after the SECOND sample instead of waiting
 *   for 5. Under strong GPS this cuts total time from ~6 s to ~2 s,
 *   which is the single biggest win against morning-rush "OTP not
 *   showing" complaints. The fallback slow path still runs when GPS is
 *   weak / disagrees / accuracy is bad.
 *
 * @param {{ onProgress?: (taken:number,total:number,lastSample?:object|null) => void,
 *           signal?: AbortSignal }} [opts]
 */
export async function getPreciseLocation(opts = {}) {
  if (!navigator.geolocation) {
    throw new Error('Location services not available on this device.');
  }
  const { onProgress, signal } = opts;
  const samples = [];

  for (let i = 0; i < SAMPLE_COUNT; i++) {
    if (signal?.aborted) break;
    const sample = await oneShot();
    if (sample) samples.push(sample);
    try { onProgress && onProgress(samples.length, SAMPLE_COUNT, sample); } catch { /* ignore */ }

    // Adaptive early-exit: two trusted samples that agree spatially → done.
    if (samples.length >= EARLY_EXIT_MIN_SAMPLES) {
      const trusted = samples.filter((s) => s.accuracy != null && s.accuracy <= TRUST_ACCURACY_M);
      if (trusted.length >= EARLY_EXIT_MIN_SAMPLES) {
        // Compare the two most recent trusted samples.
        const [a, b] = trusted.slice(-2);
        if (_distanceMeters(a, b) <= EARLY_EXIT_AGREE_METERS) {
          break;   // GPS has clearly locked on — stop sampling
        }
      }
    }

    // Don't wait after the very last reading.
    if (i < SAMPLE_COUNT - 1) {
      // First inter-sample gap is short (500ms) to give the early-exit a
      // chance to fire quickly. Subsequent gaps are the regular 1.2 s.
      const gap = i === 0 ? FAST_SAMPLE_INTERVAL_MS : SAMPLE_INTERVAL_MS;
      await new Promise((r) => setTimeout(r, gap));
    }
  }

  if (!samples.length) {
    throw new Error('Unable to get GPS location — please enable Location and try again.');
  }

  const best = pickBest(samples);
  return {
    latitude: best.latitude,
    longitude: best.longitude,
    accuracy: best.accuracy,
    samples,
  };
}
